/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Kart formları için firma-kapsamlı ek alan tanım/değer okuyucusu.
 */

import axios from '../api/axios';
import { resolveCoreApiPath } from './coreAssetUrl';
import {
  readEntityCustomFieldValue,
} from './entity-custom-field-codec';

export {
  normalizeEntityCustomFieldSubmitValue,
  normalizeEntityCustomFieldValuesForSubmit,
  readEntityCustomFieldValue,
  toEntityCustomFieldFormValue,
  toEntityCustomFieldDateTimeLocal,
} from './entity-custom-field-codec';

export interface EntityCustomFieldDefinition {
  id?: string | number;
  companyId?: string | number | null;
  entityType: string;
  code: string;
  label: string;
  description?: string | null;
  dataType?: string;
  optionsJson?: unknown;
  defaultValue?: unknown;
  isRequired?: boolean;
  showInForm?: boolean;
  showInList?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  tabKey?: string | null;
  tabLabel?: string | null;
  presentationMode?: string | null;
  placeholder?: string | null;
  colSpan?: number | null;
  rows?: number | null;
  disabled?: boolean;
}

export type EntityCustomFieldValueRow = {
  customFieldId?: string | number;
  recordId?: string | number | null;
  recordCode?: string | null;
  customField?: EntityCustomFieldDefinition;
  valueText?: unknown;
  valueNumber?: unknown;
  valueDate?: unknown;
  valueBoolean?: unknown;
  valueJson?: unknown;
  isActive?: boolean;
};

function responseItems(payload: any): any[] {
  const data = payload?.data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
}

function responseTotal(payload: any, fallback: number): number {
  const raw = payload?.data?.total ?? payload?.meta?.total;
  const total = Number(raw);
  return Number.isInteger(total) && total >= 0 ? total : fallback;
}

async function loadAllPages(
  path: string,
  params: Record<string, string | number | boolean>,
  signal?: AbortSignal,
): Promise<any[]> {
  const items: any[] = [];
  for (let page = 1; page <= 100; page += 1) {
    const response = await axios.get(resolveCoreApiPath(path), {
      params: { ...params, page, size: 500 },
      signal,
    });
    const pageItems = responseItems(response.data);
    items.push(...pageItems);
    const total = responseTotal(response.data, items.length);
    if (pageItems.length === 0 || items.length >= total) return items;
  }
  throw new Error('Ek alan API sayfalaması güvenlik sınırını aştı');
}

export async function loadEntityCustomFieldDefinitions(input: {
  entityType: string;
  companyId?: string | number | bigint | null;
  signal?: AbortSignal;
}): Promise<EntityCustomFieldDefinition[]> {
  const params: Record<string, string | number | boolean> = {
    entityType: input.entityType,
    isActive: true,
  };
  if (input.companyId !== undefined && input.companyId !== null && input.companyId !== '') {
    params.companyId = String(input.companyId);
  }
  return loadAllPages(
    '/api/entity-custom-field-values/definitions',
    params,
    input.signal,
  ) as Promise<EntityCustomFieldDefinition[]>;
}

export async function loadEntityCustomFieldValues(input: {
  entityType: string;
  recordId?: string | number;
  recordCode?: string;
  companyId?: string | number | bigint | null;
  signal?: AbortSignal;
}): Promise<Record<string, unknown>> {
  const params: Record<string, string | number | boolean> = { entityType: input.entityType };
  params.isActive = true;
  if (input.recordId !== undefined && input.recordId !== null && input.recordId !== '') {
    params.recordId = input.recordId;
  } else if (input.recordCode) {
    params.recordCode = input.recordCode;
  } else {
    throw new Error('Ek alan değeri okumak için recordId veya recordCode zorunludur');
  }
  if (input.companyId !== undefined && input.companyId !== null && input.companyId !== '') {
    params.companyId = String(input.companyId);
  }
  const rows = (await loadAllPages('/api/entity-custom-field-values', params, input.signal) as EntityCustomFieldValueRow[])
    .filter((row) => row.isActive !== false);
  return Object.fromEntries(
    rows.flatMap((row) => row.customField?.code
      ? [[row.customField.code, readEntityCustomFieldValue(row)]]
      : []),
  );
}

export async function loadEntityCustomFieldValueRowsForRecords(input: {
  entityType: string;
  recordIds: readonly (string | number | bigint)[];
  customFieldIds: readonly (string | number | bigint)[];
  companyId?: string | number | bigint | null;
  signal?: AbortSignal;
}): Promise<EntityCustomFieldValueRow[]> {
  const entityType = input.entityType.trim();
  if (!entityType) throw new Error('Ek alan değerlerini okumak için entityType zorunludur');

  const recordIds = Array.from(new Set(input.recordIds.map((id) => String(id).trim()).filter(Boolean)));
  const customFieldIds = Array.from(new Set(input.customFieldIds.map((id) => String(id).trim()).filter(Boolean)));
  if (recordIds.length === 0 || customFieldIds.length === 0) return [];
  if (recordIds.some((id) => !/^[1-9]\d*$/.test(id))) {
    throw new Error('Ek alan listesi için kayıt kimlikleri pozitif tam sayı olmalıdır');
  }
  if (customFieldIds.some((id) => !/^[1-9]\d*$/.test(id))) {
    throw new Error('Ek alan listesi için alan kimlikleri pozitif tam sayı olmalıdır');
  }

  const params: Record<string, string | number | boolean> = {
    entityType,
    recordIds: recordIds.join(','),
    customFieldIds: customFieldIds.join(','),
    isActive: true,
  };
  if (input.companyId !== undefined && input.companyId !== null && input.companyId !== '') {
    params.companyId = String(input.companyId);
  }

  const rows = await loadAllPages('/api/entity-custom-field-values', params, input.signal) as EntityCustomFieldValueRow[];
  return rows.filter((row) => row.isActive !== false);
}
